import { useEffect, useRef, useState } from "react";
import {
  getAnonId,
  getHandle,
  hasHandle,
  HandleModal,
  haptic,
  Leaderboard,
  ShareSheet,
  track,
  trackPageView,
  useLeaderboard,
} from "@karrtopelka/game-kit";
import {
  LIMBO_MAX_TARGET,
  LIMBO_MIN_TARGET,
  limboWinChance,
} from "@karrtopelka/game-api";
import { fromScoreValue } from "@karrtopelka/game-api/crash";
import type { CardProps } from "@karrtopelka/cards/types";

import { buildLimboCard } from "./lib/buildCard.js";
import { loadCardAssets } from "./lib/cardAssets.js";
import { useLimboStore, type RollResult } from "./store.js";

const QUICK_TARGETS = [1.5, 2, 5, 10, 25, 100];
/** Suspense pacing for the reveal count-up. */
const REVEAL_MS = 700;

const SIBLING_GAMES = [
  { name: "mines", hook: "dodge the boom", href: "https://mines.karrtopelka.com" },
  { name: "plinko", hook: "one ball, 470x", href: "https://plinko.karrtopelka.com" },
  { name: "crash", hook: "cash out in time", href: "https://crash.karrtopelka.com" },
] as const;

type Board = ReturnType<typeof useLeaderboard>;

export function App() {
  const phase = useLimboStore((s) => s.phase);

  // One leaderboard instance feeds both the desktop rail and the mobile board.
  const board = useLeaderboard("limbo");
  const [handle, setHandle] = useState<string | null>(() => getHandle());

  const warmed = useRef(false);
  useEffect(() => {
    if (!warmed.current && phase !== "idle") {
      warmed.current = true;
      void loadCardAssets().catch(() => {});
    }
  }, [phase]);

  useEffect(() => trackPageView("limbo"), []);
  const started = useRef(false);
  useEffect(() => {
    if (phase === "rolling" && !started.current) {
      started.current = true;
      track("limbo", "play_start");
    }
    if (phase === "done") track("limbo", "play_done");
  }, [phase]);

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center gap-5 bg-bg px-6 text-text lg:grid lg:grid-cols-[minmax(200px,260px)_minmax(0,36rem)_minmax(240px,300px)] lg:items-start lg:justify-center lg:gap-x-10 lg:gap-y-8 lg:px-10"
      style={{
        paddingTop: "calc(2.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <header className="flex animate-fade-up flex-col items-center lg:col-span-3">
        <h1 className="font-display text-3xl font-bold tracking-tight lg:text-5xl">
          limbo
          <span className="inline-block animate-breathe text-accent [text-shadow:0_0_18px_rgba(255,111,165,0.7)]">
            .
          </span>
        </h1>
        <p className="text-sm text-text-muted lg:text-base">
          call a multiplier. the dice decide.
        </p>
      </header>

      <SideRail />

      <div
        className="flex w-full animate-fade-up flex-col items-center lg:justify-self-center"
        style={{ animationDelay: "80ms" }}
      >
        <Game board={board} onHandle={setHandle} />
      </div>

      <aside
        className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-3"
        style={{ animationDelay: "240ms" }}
      >
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
          <h2 className="text-xs uppercase tracking-wide text-text-muted">
            best limbo
          </h2>
          <Leaderboard
            entries={board.entries}
            loading={board.loading}
            myHandle={handle}
            formatValue={(v) => `${fromScoreValue(v)}x`}
            emptyLabel="no calls landed yet — name your number."
            range={board.range}
            onRange={board.setRange}
          />
        </div>
      </aside>

      <footer
        className="mt-2 flex animate-fade-up flex-col items-center gap-1 text-xs text-text-faint lg:col-span-3 lg:mt-4"
        style={{ animationDelay: "320ms" }}
      >
        <div className="flex flex-col items-center gap-1 lg:hidden">
          <a
            href="https://mines.karrtopelka.com"
            className="hover:text-accent hover:underline"
          >
            also try mines — dodge the boom →
          </a>
          <a href="https://karrtopelka.com" className="hover:text-text-muted">
            karrtopelka.com
          </a>
        </div>
        <div className="hidden lg:flex lg:flex-col lg:items-center lg:gap-3">
          <p className="text-[11px] uppercase tracking-wide text-text-faint">
            more games
          </p>
          <div className="flex items-stretch gap-3">
            {SIBLING_GAMES.map((g) => (
              <a
                key={g.name}
                href={g.href}
                className="group flex min-w-[9rem] flex-col rounded-lg border border-border bg-surface/60 px-4 py-2.5 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-accent hover:shadow-glow"
              >
                <span className="font-display text-sm font-bold text-text group-hover:text-accent">
                  {g.name}
                  <span className="text-accent">.</span>
                </span>
                <span className="text-[11px] text-text-faint">{g.hook}</span>
              </a>
            ))}
          </div>
          <a
            href="https://karrtopelka.com"
            className="transition-colors duration-200 hover:text-accent"
          >
            karrtopelka.com
          </a>
        </div>
      </footer>
    </main>
  );
}

/** Desktop-only left rail: how it works, your numbers, keyboard hint. */
function SideRail() {
  const best = useLimboStore((s) => s.best);
  const history = useLimboStore((s) => s.history);
  const landed = history.filter((h) => h.won).length;

  return (
    <aside
      className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-4"
      style={{ animationDelay: "160ms" }}
    >
      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs uppercase tracking-wide text-text-muted">
          how it works
        </h2>
        <ul className="mt-2 flex flex-col gap-1.5 text-sm text-text-faint">
          <li>call a multiplier.</li>
          <li>the dice roll one number.</li>
          <li>
            roll ≥ your call — <span className="text-win">it lands</span>.
          </li>
        </ul>
      </div>

      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs uppercase tracking-wide text-text-muted">
          your best call
        </h2>
        <p className="mt-1 font-display text-3xl font-bold text-accent [text-shadow:0_0_20px_rgba(255,111,165,0.35)]">
          {best > 0 ? `${fromScoreValue(best)}x` : "—"}
        </p>
        {history.length > 0 && (
          <p className="mt-1 text-xs text-text-faint">
            last {history.length} rolls · {landed} landed
          </p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <p className="text-xs text-text-faint">
          <kbd className="rounded border border-border bg-surface-raised px-1.5 py-0.5 font-mono text-[10px] text-text-muted">
            space
          </kbd>{" "}
          or{" "}
          <kbd className="rounded border border-border bg-surface-raised px-1.5 py-0.5 font-mono text-[10px] text-text-muted">
            enter
          </kbd>{" "}
          rolls it
        </p>
      </div>
    </aside>
  );
}

function Game({
  board,
  onHandle,
}: {
  board: Board;
  onHandle: (h: string | null) => void;
}) {
  const phase = useLimboStore((s) => s.phase);
  const target = useLimboStore((s) => s.target);
  const result = useLimboStore((s) => s.result);
  const history = useLimboStore((s) => s.history);
  const best = useLimboStore((s) => s.best);
  const setTarget = useLimboStore((s) => s.setTarget);
  const startRoll = useLimboStore((s) => s.startRoll);
  const land = useLimboStore((s) => s.land);
  const reset = useLimboStore((s) => s.reset);

  const [showHandle, setShowHandle] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState<CardProps | null>(null);
  const [shownRoll, setShownRoll] = useState(0);

  // Suspense reveal: count the displayed roll up to the real one, then verdict.
  const pendingRef = useRef<RollResult | null>(null);
  useEffect(() => {
    if (phase !== "rolling") return;
    let raf = 0;
    const t0 = performance.now();
    const tick = () => {
      const p = Math.min((performance.now() - t0) / REVEAL_MS, 1);
      const res = pendingRef.current;
      if (!res) {
        // server hasn't answered yet — idle at a low churn
        setShownRoll(1 + (performance.now() % 200) / 100);
        raf = requestAnimationFrame(tick);
        return;
      }
      // ease-out toward the real roll
      const eased = 1 + (res.roll - 1) * (1 - (1 - p) ** 3);
      setShownRoll(eased);
      if (p < 1) {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (res.won) haptic.newBest();
      else haptic.tooSoon();
      land(res);
      if (res.won && res.value !== undefined) {
        setCard(
          buildLimboCard(res.target, res.value, getHandle() ?? "you", {
            total: res.total ?? 0,
            percentile: res.percentile ?? 100,
          }),
        );
        void board.refresh();
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const doRoll = async () => {
    setError(null);
    setCard(null);
    pendingRef.current = null;
    startRoll();
    try {
      const res = await fetch("/api/limbo/roll", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          anonId: getAnonId(),
          handle: getHandle(),
          target,
        }),
      });
      if (!res.ok) {
        setError(res.status === 429 ? "easy — one roll per 2s" : "roll failed");
        reset();
        return;
      }
      pendingRef.current = (await res.json()) as RollResult;
    } catch {
      setError("network hiccup — try again");
      reset();
    }
  };

  const onRoll = () => {
    if (hasHandle()) void doRoll();
    else setShowHandle(true);
  };

  // Desktop keyboard: space/enter rolls (tap-to-act game, trivially safe).
  const onRollRef = useRef(onRoll);
  onRollRef.current = onRoll;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== " " && e.key !== "Enter") return;
      if (showHandle) return;
      // Let buttons/inputs keep their native keyboard behavior.
      if (
        e.target instanceof HTMLButtonElement ||
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLAnchorElement
      )
        return;
      if (useLimboStore.getState().phase === "rolling") return;
      e.preventDefault();
      onRollRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showHandle]);

  const chance = limboWinChance(target);
  const won = result?.won === true;

  return (
    <div className="flex w-full max-w-md flex-col items-center gap-4 lg:max-w-xl">
      {/* last rolls */}
      {history.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {history.map((h, i) => (
            <span
              key={i}
              className={`rounded-full border px-2 py-0.5 font-mono text-[11px] lg:text-xs ${
                h.won ? "border-win/40 text-win" : "border-border text-text-faint"
              } ${i === 0 ? "animate-pop" : ""}`}
              title={`called ${h.target.toFixed(2)}x`}
            >
              {h.roll.toFixed(2)}x
            </span>
          ))}
        </div>
      )}

      {/* the number stage */}
      <div
        className={`relative flex aspect-square w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border bg-surface transition-shadow duration-500 lg:aspect-[16/10] ${
          phase === "done"
            ? won
              ? "animate-flash-win border-win/40 shadow-glow-win"
              : "animate-flash-bust border-danger/50"
            : "border-border"
        }`}
      >
        {/* hairline scanlines — lit-cabinet texture, ~3% */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-lg bg-[repeating-linear-gradient(0deg,rgba(255,255,255,0.028)_0,rgba(255,255,255,0.028)_1px,transparent_1px,transparent_3px)]"
        />
        {phase === "idle" && (
          <>
            <span className="font-display text-6xl font-extrabold text-text-faint lg:text-7xl">
              {target.toFixed(2)}x
            </span>
            <span className="text-sm text-text-muted">
              your call · win chance{" "}
              <span className="font-mono text-text">{(chance * 100).toFixed(1)}%</span>
            </span>
            {best > 0 && (
              <span className="text-xs text-text-faint">
                best call landed: {fromScoreValue(best)}x
              </span>
            )}
          </>
        )}
        {phase === "rolling" && (
          <>
            <span className="animate-breathe font-display text-6xl font-extrabold text-text lg:text-7xl">
              {shownRoll.toFixed(2)}x
            </span>
            <span className="text-sm text-text-faint">rolling…</span>
          </>
        )}
        {phase === "done" && result && (
          <>
            <span
              className={`font-display text-7xl font-extrabold lg:text-8xl ${
                won
                  ? "text-win [text-shadow:0_0_36px_rgba(0,232,123,0.4)]"
                  : "text-danger [text-shadow:0_0_36px_rgba(255,51,85,0.35)]"
              }`}
            >
              {result.roll.toFixed(2)}x
            </span>
            <span className="text-sm text-text-muted">
              you called{" "}
              <span className={`font-mono font-bold ${won ? "text-win" : "text-text"}`}>
                {result.target.toFixed(2)}x
              </span>{" "}
              — {won ? "it landed 🎯" : "the dice said no"}
            </span>
            {won && result.total !== undefined && result.total >= 10 && (
              <p className="text-sm text-text">
                #{result.rank} of {result.total}
                <span className="text-accent"> · top {result.percentile}%</span>
              </p>
            )}
          </>
        )}
      </div>

      {/* target picker */}
      {phase !== "rolling" && (
        <div className="flex w-full flex-col items-center gap-2">
          <div className="flex flex-wrap justify-center gap-1.5">
            {QUICK_TARGETS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  haptic.tap();
                  setTarget(t);
                  if (phase === "done") reset();
                }}
                className={`rounded-full border px-3 py-1 font-mono text-sm transition-all duration-200 ${
                  target === t
                    ? "border-accent bg-accent text-bg shadow-glow"
                    : "border-border text-text-muted hover:-translate-y-0.5 hover:border-accent hover:text-text"
                }`}
              >
                {t}x
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-text-faint">
            or name it:
            <input
              type="number"
              min={LIMBO_MIN_TARGET}
              max={LIMBO_MAX_TARGET}
              step="0.01"
              value={target}
              onChange={(e) => {
                const v = Number.parseFloat(e.target.value);
                if (Number.isFinite(v)) {
                  setTarget(
                    Math.min(
                      Math.max(Math.round(v * 100) / 100, LIMBO_MIN_TARGET),
                      LIMBO_MAX_TARGET,
                    ),
                  );
                  if (phase === "done") reset();
                }
              }}
              className="w-24 rounded-md border border-border bg-bg px-2 py-1 font-mono text-sm text-text outline-none transition-all duration-200 hover:border-accent/60 focus:border-accent focus:shadow-glow"
            />
            <span className="font-mono">x</span>
          </label>
        </div>
      )}

      {error && <p className="text-sm text-accent">{error}</p>}

      {phase !== "rolling" && (
        <button
          type="button"
          onClick={onRoll}
          className="animate-neon-pulse w-full rounded-md bg-accent px-4 py-4 text-lg font-bold text-bg transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent active:scale-[0.99] lg:text-xl"
        >
          {phase === "done" ? "roll it again" : `ROLL · need ${target.toFixed(2)}x+`}
        </button>
      )}

      {phase === "done" && won && card && (
        <ShareSheet
          card={card}
          replyLink="limbo.karrtopelka.com/r"
          caption={`called ${result?.target.toFixed(2)}x, it rolled ${result?.roll.toFixed(2)}x. ${card.provocation}`}
          loadAssets={loadCardAssets}
        />
      )}

      {/* mobile board — desktop shows the persistent right rail instead */}
      <section className="flex w-full flex-col items-center gap-3 border-t border-border pt-6 lg:hidden">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          boldest calls landed
        </h2>
        <Leaderboard
          entries={board.entries}
          loading={board.loading}
          myHandle={getHandle()}
          formatValue={(v) => `${fromScoreValue(v)}x`}
          emptyLabel="no calls landed yet — name your number."
          range={board.range}
          onRange={board.setRange}
        />
      </section>

      {showHandle && (
        <HandleModal
          onCancel={() => setShowHandle(false)}
          onDone={(h) => {
            setShowHandle(false);
            onHandle(h);
            void doRoll();
          }}
        />
      )}
    </div>
  );
}
